import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import { formatDistanceToNowStrict, isToday, isYesterday } from 'date-fns';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import EmptyState from '@atlaskit/empty-state';
import WorldIcon from '@atlaskit/icon/glyph/world';
import FileIcon from '@atlaskit/icon/glyph/file';
import PageIcon from '@atlaskit/icon/glyph/page';
import PdfIcon from '@atlaskit/icon/glyph/pdf';
import CreditcardIcon from '@atlaskit/icon/glyph/creditcard';
import EmailIcon from '@atlaskit/icon/glyph/email';
import { EntitlementIcon, useRecentlyViewed } from '@css-xp/base';
import { token } from '@atlaskit/tokens';
import { getEntityName } from '@css-xp/ppc-utils';
import { RecentlyViewedEntityEnum } from '@css-xp/ppc-constants';
import { recentlyViewedDrawerInitialState } from '@css-xp/ppc-common/src/state-props';
import { RecentlyViewedData } from '@atlassian/cssxp-typescommon';
import { EntitlementIconWrapper } from './recently-viewed-drawer.styles';

interface RecentlyViewedDrawerProps {
  searchInput?: string;
  txaId: string;
}

export type RecentlyViewedMetaData = {
  id: string;
  timestamp: number;
  entityName?: string;
  pageTitle?: string;
  metadata?: {
    id: string;
  };
};

const Container = styled.div`
  border-radius: 6px;
  background: ${token('elevation.surface.overlay')};
  box-shadow: ${token('elevation.shadow.overlay')};
  .line-ellipsis {
    width: 400px;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const TitleStyleContainer = styled.span`
  color: ${token('color.text')};
`;

export enum RecentlyViewedEntityNamesEnum {
  RECENT_SUBSCRIPTIONS = 'RECENT SUBSCRIPTIONS',
  RECENT_CLOUD_SITES = 'RECENT SITES',
  RECENT_QUOTES = 'RECENT QUOTES',
  RECENT_ORDERS = 'RECENT ORDERS',
  RECENT_INVOICES = 'RECENT INVOICES',
  RECENT_REFUNDS = 'RECENT REFUNDS',
  RECENT_BILLING_PROFILES = 'RECENT BILLING PROFILES',
  RECENT_EMAILS = 'RECENT EMAILS',
}

const getLastViewedTime = (timestamp: string) => {
  if (isToday(new Date(timestamp))) {
    return 'today';
  }
  if (isYesterday(new Date(timestamp))) {
    return 'yesterday';
  }
  return formatDistanceToNowStrict(new Date(timestamp), { addSuffix: true });
};

const formatRecentlyViewedData = (data: RecentlyViewedData<RecentlyViewedMetaData>[]) =>
  data?.map((item) => ({
    ...item,
    ...item?.metadata,
  }));

export const RecentlyViewedDrawer = ({ searchInput, txaId }: RecentlyViewedDrawerProps) => {
  const { getRecentlyViewed } = useRecentlyViewed();
  const history = useHistory();

  const getRecentlyVisitedData = useCallback((entityName) => {
    return getRecentlyViewed<RecentlyViewedMetaData>(getEntityName(entityName, txaId))?.recentlyVisitedData;
  }, []);

  const recentlyViewedItemsData = React.useMemo(() => {
    return [
      {
        sectionTitle: RecentlyViewedEntityNamesEnum.RECENT_SUBSCRIPTIONS,
        recentlyVisitedData: getRecentlyVisitedData(RecentlyViewedEntityEnum.SUBSCRIPTION),
        titleKey: 'productName',
        subTitleKey: 'entitlementNumber',
      },
      {
        sectionTitle: RecentlyViewedEntityNamesEnum.RECENT_CLOUD_SITES,
        recentlyVisitedData: getRecentlyVisitedData(RecentlyViewedEntityEnum.CLOUD_SITE),
        titleKey: 'cloudSiteUrl',
        subTitleKey: 'companyName',
        iconBefore: <WorldIcon label="cloud-site-icon" />,
      },
      {
        sectionTitle: RecentlyViewedEntityNamesEnum.RECENT_QUOTES,
        recentlyVisitedData: getRecentlyVisitedData(RecentlyViewedEntityEnum.QUOTE),
        titleKey: 'quoteNumber',
        subTitleKey: 'companyName',
        iconBefore: <FileIcon label="quote-icon" />,
      },
      {
        sectionTitle: RecentlyViewedEntityNamesEnum.RECENT_INVOICES,
        recentlyVisitedData: getRecentlyVisitedData(RecentlyViewedEntityEnum.INVOICE),
        titleKey: 'invoiceNumber',
        subTitleKey: 'companyName',
        iconBefore: <PdfIcon label="invoice-icon" />,
      },
      {
        sectionTitle: RecentlyViewedEntityNamesEnum.RECENT_ORDERS,
        recentlyVisitedData: getRecentlyVisitedData(RecentlyViewedEntityEnum.ORDER),
        titleKey: 'orderNumber',
        subTitleKey: 'companyName',
        iconBefore: <PageIcon label="order-icon" />,
      },
      {
        sectionTitle: RecentlyViewedEntityNamesEnum.RECENT_BILLING_PROFILES,
        recentlyVisitedData: getRecentlyVisitedData(RecentlyViewedEntityEnum.BILLING_PROFILE),
        titleKey: 'billingProfileName',
        subTitleKey: 'companyName',
        iconBefore: <CreditcardIcon label="billing-profile-icon" />,
      },
      {
        sectionTitle: RecentlyViewedEntityNamesEnum.RECENT_EMAILS,
        recentlyVisitedData: getRecentlyVisitedData(RecentlyViewedEntityEnum.EMAIL),
        titleKey: 'emailSubject',
        subTitleKey: 'companyName',
        iconBefore: <EmailIcon label="email-icon" />,
      },
      {
        sectionTitle: RecentlyViewedEntityNamesEnum.RECENT_REFUNDS,
        recentlyVisitedData: getRecentlyVisitedData(RecentlyViewedEntityEnum.REFUND),
        titleKey: 'refundNumber',
        subTitleKey: 'companyName',
        iconBefore: <PdfIcon label="invoice-icon" />,
      },
    ];
  }, []);

  const filterDataBySearchInput = useCallback(
    (data: Record<string, string>[] | null, key = '') =>
      data?.filter((item) => item[key]?.toLowerCase()?.includes(searchInput?.toLowerCase() ?? '')),
    [searchInput]
  );

  const renderRecentlyViewedItems = ({
    recentlyVisitedData,
    titleKey,
    subTitleKey,
    sectionTitle,
    iconBefore = null,
  }: {
    titleKey: string;
    subTitleKey: string;
    sectionTitle: RecentlyViewedEntityNamesEnum;
    iconBefore?: React.ReactNode;
    recentlyVisitedData: RecentlyViewedData<RecentlyViewedMetaData>[];
  }) => {
    const data = formatRecentlyViewedData(recentlyVisitedData);
    const sortedByTimestamp = data?.sort((a, b) => b.timestamp - a.timestamp);
    const filteredData = filterDataBySearchInput(sortedByTimestamp as [], titleKey)?.slice(0, 3);

    return filteredData && filteredData?.length > 0 ? (
      <Section title={sectionTitle}>
        {filteredData.map((item) => {
          return (
            <ButtonItem
              key={sectionTitle}
              onClick={() => {
                item?.url && history.push(item.url);
              }}
              description={<span className="line-ellipsis">{item[subTitleKey]}</span>}
              iconAfter={`Viewed ${getLastViewedTime(item?.timestamp)}`}
              iconBefore={
                iconBefore || (
                  <EntitlementIconWrapper>
                    <EntitlementIcon
                      productId={item?.productId ?? ''}
                      isCloud={true}
                      className="avatar medium entitlement-icon"
                      logoId={item?.logoId ?? ''}
                    />
                  </EntitlementIconWrapper>
                )
              }
            >
              <span className="line-ellipsis">
                <TitleStyleContainer>{item[titleKey]}</TitleStyleContainer>
              </span>
            </ButtonItem>
          );
        })}
      </Section>
    ) : (
      <></>
    );
  };

  const isEmptyState = !searchInput && recentlyViewedItemsData.every((data) => !data.recentlyVisitedData?.length);
  const menuMaxHeight = global.window.innerHeight - 200;

  return (
    <Container>
      <MenuGroup maxHeight={isEmptyState ? 400 : menuMaxHeight}>
        {isEmptyState ? (
          <Section>
            <EmptyState {...recentlyViewedDrawerInitialState} />
          </Section>
        ) : (
          recentlyViewedItemsData.map((data) => renderRecentlyViewedItems(data))
        )}
      </MenuGroup>
    </Container>
  );
};
